import { EmptyState, Table, type TableColumn } from '@today/ui-components'
import { Badge, Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ContentConfirmDialogView } from './content-confirm-dialog.view'
import { CheckboxInput, ErrorText, TextInput } from './content-fields'
import type { Translate, TypeFormView, TypeRowView } from './content-types'

/**
 * Props for {@link TypesView}.
 *
 * @property roles - Every `Role` value, offered as approver-role checkboxes.
 * @property locales - Required-language options.
 * @property form - The create/edit dialog; `id` is null when creating.
 * @property deleting - The type whose delete confirmation is open.
 */
export interface TypesViewProps {
	t: Translate
	rows: TypeRowView[]
	roles: string[]
	locales: string[]
	error: boolean
	form: { id: string | null; values: TypeFormView; errors: Record<string, string>; formError: string | null } | null
	deleting: { code: string; error: string | null } | null
	isBusy: boolean
	onNew: () => void
	onEdit: (id: string) => void
	onDelete: (id: string) => void
	onChange: (field: keyof TypeFormView, value: string | boolean | string[]) => void
	onSubmit: () => void
	onCancel: () => void
	onConfirmDelete: () => void
	onCancelDelete: () => void
	onRetry: () => void
}

const yes = (t: Translate, value: boolean) => (value ? t('types.yes') : t('types.no'))

const columns = (props: TypesViewProps): TableColumn<TypeRowView>[] => {
	const { t } = props
	return [
		{ key: 'code', header: t('types.columns.code') },
		{
			key: 'name',
			header: t('types.columns.name'),
			cell: (row) => (
				<span className="flex items-center gap-2">
					{row.name}
					{row.isSystem ? <Badge variant="secondary">{t('types.system')}</Badge> : null}
				</span>
			),
		},
		{ key: 'requiresApproval', header: t('types.columns.approval'), cell: (row) => yes(t, row.requiresApproval) },
		{ key: 'approverRoles', header: t('types.columns.approvers'), cell: (row) => row.approverRoles.join(', ') },
		{ key: 'forbidSelfApproval', header: t('types.columns.fourEyes'), cell: (row) => yes(t, row.forbidSelfApproval) },
		{ key: 'requiredLocales', header: t('types.columns.required'), cell: (row) => row.requiredLocales.join(', ') },
		{ key: 'allowLocaleFallback', header: t('types.columns.fallback'), cell: (row) => yes(t, row.allowLocaleFallback) },
		{
			key: 'actions',
			header: t('types.columns.actions'),
			cell: (row) => (
				<span className="flex gap-2">
					<Button size="sm" variant="muted" onPress={() => props.onEdit(row.id)}>
						{t('types.edit')}
					</Button>
					{row.isSystem ? null : (
						<Button size="sm" variant="danger" onPress={() => props.onDelete(row.id)}>
							{t('types.delete')}
						</Button>
					)}
				</span>
			),
		},
	]
}

const toggle = (list: string[], value: string, on: boolean) =>
	on ? [...list.filter((x) => x !== value), value] : list.filter((x) => x !== value)

function CheckboxGroup(props: {
	legend: string
	options: string[]
	value: string[]
	error?: string
	onChange: (value: string[]) => void
}): ReactElement {
	return (
		<fieldset className="flex flex-col gap-1">
			<legend className="text-ds-sm text-ds-text-muted">{props.legend}</legend>
			{props.options.map((option) => (
				<CheckboxInput
					key={option}
					label={option}
					checked={props.value.includes(option)}
					onChange={(on) => props.onChange(toggle(props.value, option, on))}
				/>
			))}
			<ErrorText message={props.error} />
		</fieldset>
	)
}

function RuleFields({
	props,
	values,
	errors,
}: {
	props: TypesViewProps
	values: TypeFormView
	errors: Record<string, string>
}): ReactElement {
	const { t, onChange } = props
	return (
		<>
			<CheckboxInput
				label={t('types.form.requiresApproval')}
				checked={values.requiresApproval}
				onChange={(v) => onChange('requiresApproval', v)}
			/>
			<CheckboxGroup
				legend={t('types.form.approverRoles')}
				options={props.roles}
				value={values.approverRoles}
				error={errors.approverRoles}
				onChange={(v) => onChange('approverRoles', v)}
			/>
			<CheckboxInput
				label={t('types.form.fourEyes')}
				checked={values.forbidSelfApproval}
				onChange={(v) => onChange('forbidSelfApproval', v)}
			/>
			<CheckboxGroup
				legend={t('types.form.requiredLocales')}
				options={props.locales}
				value={values.requiredLocales}
				error={errors.requiredLocales}
				onChange={(v) => onChange('requiredLocales', v)}
			/>
			<CheckboxInput
				label={t('types.form.fallback')}
				checked={values.allowLocaleFallback}
				onChange={(v) => onChange('allowLocaleFallback', v)}
			/>
		</>
	)
}

function TypeForm({ props }: { props: TypesViewProps }): ReactElement | null {
	const { t, form, onChange } = props
	if (!form) return null
	const { values, errors } = form
	return (
		<div className="flex flex-col gap-3">
			<ErrorText message={form.formError} />
			{form.id === null ? (
				<TextInput
					label={t('types.form.code')}
					value={values.code}
					error={errors.code}
					onChange={(v) => onChange('code', v)}
				/>
			) : null}
			<TextInput
				label={t('types.form.name')}
				value={values.name}
				error={errors.name}
				onChange={(v) => onChange('name', v)}
			/>
			<TextInput
				label={t('types.form.description')}
				value={values.description}
				error={errors.description}
				onChange={(v) => onChange('description', v)}
			/>
			<RuleFields props={props} values={values} errors={errors} />
			<div className="flex gap-2">
				<Button isDisabled={props.isBusy} onPress={props.onSubmit}>
					{t('types.form.save')}
				</Button>
				<Button variant="muted" onPress={props.onCancel}>
					{t('actions.cancel')}
				</Button>
			</div>
		</div>
	)
}

/** Content types: list with the system badge, create/edit form and protected delete (organism). */
export function TypesView(props: TypesViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-4">
			<header className="flex items-center justify-between">
				<h1 className="text-ds-xl font-semibold text-ds-text">{t('types.title')}</h1>
				<Button onPress={props.onNew}>{t('types.new')}</Button>
			</header>
			{props.error ? (
				<EmptyState title={t('types.error')} action={{ label: t('actions.retry'), onPress: props.onRetry }} />
			) : (
				<Table columns={columns(props)} rows={props.rows} />
			)}
			<Dialog
				isOpen={props.form !== null}
				onOpenChange={(open) => (open ? undefined : props.onCancel())}
				title={props.form?.id ? t('types.editTitle') : t('types.newTitle')}
				labels={{ close: t('actions.close') }}
			>
				<TypeForm props={props} />
			</Dialog>
			<ContentConfirmDialogView
				t={t}
				isOpen={props.deleting !== null}
				title={t('types.deleteTitle')}
				body={t('types.deleteBody', { code: props.deleting?.code ?? '' })}
				confirmLabel={t('types.delete')}
				danger
				isBusy={props.isBusy}
				error={props.deleting?.error}
				onConfirm={props.onConfirmDelete}
				onCancel={props.onCancelDelete}
			/>
		</section>
	)
}
